import React, { useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { type IconProps } from '@atlaskit/icon/types';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePostOfficePlacement } from '@atlassian/conversation-assistant-utils/usePostOfficePlacement';

import { type ConversationAction, type SkillMetadataMap } from '../../../../common/types';
import { DynamicSurfaceActionItem } from '../../../../common/ui/action-item';
import { ActionItemSkeleton } from '../../../../common/ui/action-item-skeleton';
import { isPlaceholderAction } from '../../../../common/utils/create-placeholder-action';
import { orderRankedActions } from '../../../../common/utils/order-ranked-actions';

const styles = cssMap({
	actionContainerStyles: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.050'),
		paddingTop: token('space.050'),
		width: '100%',
	},
});

export const i18n: {
	showMoreActionText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	showMoreActionText: {
		id: 'rovo-conversation-actions.actions.show-more-action-text',
		defaultMessage: 'Show more',
		description: 'The action text for the show more action in rovo conversation actions',
	},
});

export type DiscoverPromptLibraryButtonProps = {
	title: string;
	icon: (props: IconProps) => React.JSX.Element;
	onClick: () => void;
	badge?: React.ReactNode;
};

const ErrorFallback: React.FunctionComponent = () => <></>;

type ConversationActionCompactViewProps = {
	actions: ConversationAction[];
	showMoreButton?: boolean;
	handleShowMore: () => void;
	handleShowMoreHover: () => void;
	handleShowMoreLeave: () => void;
	actionRankingOrder?: string[] | null;
	handleAction: (action: ConversationAction, index?: number) => void;
	handleActionHover: (action: ConversationAction, index?: number) => void;
	handleActionLeave: () => void;
	skillMetadataMap?: SkillMetadataMap;
	discoverPromptLibraryButton?: DiscoverPromptLibraryButtonProps;
};

export const ConversationActionCompactView = ({
	actions,
	showMoreButton,
	handleShowMore,
	handleShowMoreHover,
	handleShowMoreLeave,
	actionRankingOrder,
	handleAction,
	handleActionHover,
	handleActionLeave,
	skillMetadataMap,
	discoverPromptLibraryButton,
}: ConversationActionCompactViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { PostOfficePlacement } = usePostOfficePlacement();

	const MAX_VISIBLE_ACTIONS = 4;

	const orderedActions = useMemo(() => {
		if (actionRankingOrder && actionRankingOrder.length > 0) {
			return showMoreButton
				? orderRankedActions(actionRankingOrder, actions).slice(0, MAX_VISIBLE_ACTIONS)
				: orderRankedActions(actionRankingOrder, actions);
		}
		// If no valid ranking order, apply the same slicing logic based on showMoreButton
		return showMoreButton ? actions.slice(0, MAX_VISIBLE_ACTIONS) : actions;
	}, [actionRankingOrder, actions, showMoreButton]);

	return (
		<Box xcss={styles.actionContainerStyles}>
			<Stack role="list">
				{orderedActions.length > 0 && (
					<StaggeredEntrance columns={1}>
						<>
							{orderedActions.map((action, index) => {
								if (isPlaceholderAction(action)) {
									return <ActionItemSkeleton key={action.id} action={action} />;
								}

								const actionItem = (
									<FadeIn key={`action-item-compact-view-${index}`} duration="large">
										{(props) => (
											<div {...props} role="listitem">
												<DynamicSurfaceActionItem
													key={action.id}
													title={action.title}
													icon={action.icon}
													onClick={() => handleAction(action, index)}
													onMouseEnter={() => handleActionHover(action, index)}
													onMouseLeave={handleActionLeave}
													ref={action.ref}
													href={action.href}
													action={action}
													skillMetadataMap={skillMetadataMap}
													badge={action.badge}
												/>
											</div>
										)}
									</FadeIn>
								);

								if (showMoreButton && index === orderedActions.length - 1) {
									return (
										<React.Fragment key={`compact-actions-with-show-more-${index}`}>
											{actionItem}
											{discoverPromptLibraryButton ? (
												<FadeIn key="discover-prompt-library-button" duration="large">
													{(props) => (
														<div {...props} role="listitem">
															<div data-spotlight-target="rovo-chat-browse-prompt-gallery">
																<DynamicSurfaceActionItem
																	key="discover-prompt-library"
																	title={discoverPromptLibraryButton.title}
																	icon={discoverPromptLibraryButton.icon}
																	onClick={discoverPromptLibraryButton.onClick}
																	badge={discoverPromptLibraryButton.badge}
																/>
															</div>
														</div>
													)}
												</FadeIn>
											) : (
												<></>
											)}
											<FadeIn key={`show-more-button`} duration="large">
												{(props) => (
													<div {...props} role="listitem">
														<DynamicSurfaceActionItem
															key={'show-more-ai-action'}
															title={formatMessage(i18n.showMoreActionText)}
															icon={() => (
																<ChevronDownIcon label="" color={token('color.icon.subtlest')} />
															)}
															onClick={handleShowMore}
															onMouseEnter={handleShowMoreHover}
															onMouseLeave={handleShowMoreLeave}
														/>
													</div>
												)}
											</FadeIn>
										</React.Fragment>
									);
								}

								return actionItem;
							})}
							{!showMoreButton && discoverPromptLibraryButton ? (
								<FadeIn key="discover-prompt-library-button" duration="large">
									{(props) => (
										<div {...props} role="listitem">
											<div data-spotlight-target="rovo-chat-browse-prompt-gallery">
												<DynamicSurfaceActionItem
													key="discover-prompt-library"
													title={discoverPromptLibraryButton.title}
													icon={discoverPromptLibraryButton.icon}
													onClick={discoverPromptLibraryButton.onClick}
													badge={discoverPromptLibraryButton.badge}
												/>
											</div>
										</div>
									)}
								</FadeIn>
							) : (
								<></>
							)}
						</>
					</StaggeredEntrance>
				)}
			</Stack>
			{discoverPromptLibraryButton && PostOfficePlacement && (
				<PostOfficePlacement
					placementId="rovo-chat-browse-prompt-gallery"
					ErrorFallback={<ErrorFallback />}
				/>
			)}
		</Box>
	);
};
